<section class="dashboard">
  <section class="hero">
    <div class="hero__content">
      <span class="hero__label">
        {{ i18n.t('dashboard.farmStatus') }}
      </span>

      <h2>{{ i18n.t('dashboard.runningSmoothly') }}</h2>

      <p>
        {{ i18n.t('dashboard.serverUpToDate') }}
      </p>

      <div class="hero__meta">
        <div>
          <span class="hero__indicator"></span>
          {{ i18n.t('dashboard.serverConnected') }}
        </div>
      </div>
    </div>

    <div class="hero__visual">
      @if (gameState(); as state) {
        @if (displayedGameState(); as displayedState) {
          <div class="hero__game-time">
            <span class="hero__game-time-month">
              {{ gameMonthLabel(displayedState.period) }}
            </span>

            <span class="hero__game-time-day">
              {{ i18n.t('gameState.day') }}
              {{ displayedState.day }}/{{ state.daysPerMonth }}
            </span>

            <strong class="hero__game-time-clock">
              {{ gameTimeLabel(displayedState.currentHour) }}
              {{ i18n.t('gameState.clock') }}
            </strong>
          </div>
        }
      }

      <div class="hero__ring">
        <strong>
          {{ totalArea | number: '1.1-1' }}
        </strong>
        <span>ha</span>
      </div>
    </div>
  </section>

  <section class="metrics">
    <article class="metric">
      <div class="metric__top">
        <span>{{ i18n.t('dashboard.fields') }}</span>
        <span class="metric__icon">F</span>
      </div>

      <strong>{{ totalFields }}</strong>
      <small>{{ i18n.t('dashboard.totalFields') }}</small>
    </article>

    <article class="metric">
      <div class="metric__top">
        <span>{{ i18n.t('dashboard.totalArea') }}</span>
        <span class="metric__icon">A</span>
      </div>

      <strong>
        {{ totalArea | number: '1.1-1' }}
        <small>ha</small>
      </strong>

      <small>
        {{ i18n.t('dashboard.managedFarmland') }}
      </small>
    </article>

    <article class="metric">
      <div class="metric__top">
        <span>{{ i18n.t('dashboard.openTasks') }}</span>
        <span class="metric__icon metric__icon--warning">
          T
        </span>
      </div>

      <strong>{{ openTasks }}</strong>
      <small>{{ i18n.t('dashboard.planningItems') }}</small>
    </article>

    <article class="metric">
      <div class="metric__top">
        <span>{{ i18n.t('dashboard.nextHarvest') }}</span>
        <span class="metric__icon metric__icon--success">
          H
        </span>
      </div>

      @if (nextHarvestField; as field) {
        <strong>
          {{ i18n.t('field.field') }}
          {{ field.number }}
        </strong>

        <small>
          @if (field.fruitTypeName; as fruitTypeName) {
            <app-crop-icon [crop]="fruitTypeName" />
            {{ cropLabel(fruitTypeName) }}
          } @else {
            {{ i18n.t('crop.none') }}
          }

          ·
          {{ i18n.t('field.growthStatus.harvestable') }}
        </small>
      } @else {
        <strong>
          {{ i18n.t('dashboard.noHarvestReady') }}
        </strong>

        <small>
          {{ i18n.t('dashboard.noHarvestReadyHint') }}
        </small>
      }
    </article>
  </section>

  <div class="dashboard-grid">
    <section class="panel fields">
      <header class="panel__header">
        <div>
          <h2>{{ i18n.t('dashboard.fields') }}</h2>
          <p>
            {{ i18n.t('dashboard.currentFieldStatus') }}
          </p>
        </div>

        <a routerLink="/fields">
          {{ i18n.t('dashboard.viewAll') }}
        </a>
      </header>

      @if (loading()) {
        <div class="panel__state">
          {{ i18n.t('dashboard.loadingFields') }}
        </div>
      } @else if (error()) {
        <div class="panel__state panel__state--error">
          {{ i18n.t('dashboard.loadFieldsError') }}
        </div>
      } @else if (fields().length === 0) {
        <div class="panel__state panel__state--empty">
          <strong>
            {{ i18n.t('dashboard.noFields') }}
          </strong>

          <p>
            {{ i18n.t('dashboard.noFieldsHint') }}
          </p>

          <a
            class="dashboard__action"
            routerLink="/fields"
          >
            <span>+</span>
            {{ i18n.t('dashboard.addField') }}
          </a>
        </div>
      } @else {
        <div class="field-list">
          @for (field of fields(); track field.id) {
            <article class="field-row">
              <div class="field-row__info">
                <strong>
                  {{ i18n.t('field.field') }}
                  {{ field.number }}
                </strong>

                <span>
                  @if (field.fruitTypeName; as fruitTypeName) {
                    <app-crop-icon [crop]="fruitTypeName" />
                    {{ cropLabel(fruitTypeName) }}
                  } @else {
                    {{ i18n.t('crop.none') }}
                  }

                  @if (field.plannedCrop; as plannedCrop) {
                    · {{ i18n.t('field.planned') }}:
                    <app-crop-icon [crop]="plannedCrop.code" />
                    {{ cropLabel(plannedCrop.code) }}
                  }

                  ·
                  {{ field.area | number: '1.1-1' }} ha
                </span>
              </div>

              <span
                class="field-status"
                [class.field-status--growing]="field.growthStatus === 'GROWING'"
                [class.field-status--harvestable]="field.growthStatus === 'HARVESTABLE'"
                [class.field-status--cut]="field.growthStatus === 'CUT'"
                [class.field-status--unknown]="field.growthStatus === 'UNKNOWN'"
              >
                @switch (field.growthStatus) {
                  @case ('GROWING') {
                    {{ i18n.t('field.growthStatus.growing') }}
                  }
                  @case ('HARVESTABLE') {
                    {{ i18n.t('field.growthStatus.harvestable') }}
                  }
                  @case ('CUT') {
                    {{ i18n.t('field.growthStatus.cut') }}
                  }
                  @default {
                    {{ i18n.t('field.growthStatus.unknown') }}
                  }
                }
              </span>

              @if (calendarInfo(field); as info) {
                @if (info.status !== 'none') {
                  <span
                    class="badge badge-sm"
                    [class.badge-success]="info.status === 'harvest-ready'"
                    [class.badge-warning]="info.status === 'harvest-window'"
                    [class.badge-info]="info.status === 'sowing-window'"
                    [title]="info.description"
                  >
                    {{ info.label }}
                  </span>
                }
              }

              <span class="field-row__arrow">→</span>
            </article>
          }
        </div>
      }
    </section>

    <aside class="side-column">
      <section class="panel work">
        <header class="panel__header">
          <div>
            <h2>{{ i18n.t('dashboard.nextUp') }}</h2>
            <p>{{ i18n.t('dashboard.plannedWork') }}</p>
          </div>

          <button
            class="dashboard__action dashboard__action--icon"
            type="button"
            (click)="openPlanning()"
            [attr.aria-label]="i18n.t('dashboard.newPlanning')"
            [attr.title]="i18n.t('dashboard.newPlanning')"
          >
            <span>+</span>
          </button>
        </header>

        @if (nextTasks.length > 0) {
          <div class="work-list">
            @for (task of nextTasks; track task.id) {
              <article class="work-item">
                <div class="work-item__icon">
                  @switch (task.type) {
                    @case ('sow') {
                      <svg
                        lucideSprout
                        [size]="18"
                        [strokeWidth]="1.8"
                      ></svg>
                    }
                    @case ('fertilize') {
                      <svg
                        lucideDroplets
                        [size]="18"
                        [strokeWidth]="1.8"
                      ></svg>
                    }
                    @case ('harvest') {
                      <svg
                        lucideWheat
                        [size]="18"
                        [strokeWidth]="1.8"
                      ></svg>
                    }
                    @case ('roll') {
                      <svg
                        lucideCircleDot
                        [size]="18"
                        [strokeWidth]="1.8"
                      ></svg>
                    }
                    @case ('cultivate') {
                      <svg
                        lucideTractor
                        [size]="18"
                        [strokeWidth]="1.8"
                      ></svg>
                    }
                    @case ('plow') {
                      <svg
                        lucideShovel
                        [size]="18"
                        [strokeWidth]="1.8"
                      ></svg>
                    }
                    @default {
                      <svg
                        lucideTractor
                        [size]="18"
                        [strokeWidth]="1.8"
                      ></svg>
                    }
                  }
                </div>

                <div class="work-item__content">
                  <div class="work-item__top">
                    <strong class="work-item__title">
                      {{ taskLabel(task.type) }}

                      @if (task.field) {
                        <span class="work-item__separator">
                          ·
                        </span>

                        <span>
                          {{ i18n.t('field.field') }}
                          {{ task.field.number }}
                        </span>
                      }
                    </strong>

                    <span class="work-item__status">
                      {{ taskStatusLabel(task.status) }}
                    </span>
                  </div>

                  @if (task.type === 'sow' && task.crop) {
                    <span class="work-item__meta">
                      <app-crop-icon [crop]="task.crop.code" />
                      {{ cropLabel(task.crop.code) }}
                    </span>
                  }
                </div>
              </article>
            }
          </div>
        } @else {
          <div class="panel__state">
            {{ i18n.t('task.noOpenTasks') }}
          </div>
        }
      </section>

      <section class="sync-card">
        <div class="sync-card__icon">↻</div>

        <div>
          <strong>
            {{ i18n.t('dashboard.liveSync') }}
          </strong>

          <span>
            {{
              i18n.t(
                'dashboard.dedicatedServerConnected'
              )
            }}
          </span>
        </div>

        <span class="sync-card__status"></span>
      </section>
    </aside>
  </div>

  <app-dialog
    [open]="planningOpen()"
    [title]="i18n.t('task.form.title')"
    (closed)="closePlanning()"
  >
    @if (taskError()) {
      <div class="alert alert-error mb-3" role="alert">
        <span>{{ taskError() }}</span>
      </div>
    }

    <app-task-form
      [fields]="fields()"
      (submitted)="createTask($event)"
      (cancelled)="closePlanning()"
    />
  </app-dialog>
</section>
